import { FileUploader } from '@/components/shared/file-uploader';
import { useNotification } from '@/context/notification';

const MIN_DIMENSION = 100;
const DEFAULT_MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB default
const ACCEPTED_IMAGE_FORMATS =
  'image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml';

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)}KB`;
  }
  return `${Math.round(bytes / (1024 * 1024))}MB`;
};

const getImageDimensions = async (
  file: File
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    try {
      const url = URL.createObjectURL(file);
      const img = new Image();

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error(`Failed to load image: ${file.type}`));
      };

      img.src = url;
    } catch (error) {
      reject(error);
    }
  });
};

const createBase64Image = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

type ImageUploaderProps = {
  children: React.ReactNode;
  minDimension?: number;
  maxFileSize?: number;
  onUpload: (image: string | null) => void;
};

export const ImageUploader = ({
  children,
  minDimension = MIN_DIMENSION,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  onUpload,
}: ImageUploaderProps) => {
  const { toast } = useNotification();
  const MIN_DIMENSION_ERROR_MESSAGE = `Imagem deve ter no mínimo ${minDimension} x ${minDimension} pixels`;
  const MAX_FILE_SIZE_ERROR_MESSAGE = `O tamanho máximo do arquivo é ${formatFileSize(maxFileSize)}`;

  const validation = async (file: File) => {
    const acceptedFormats = ACCEPTED_IMAGE_FORMATS.split(',');
    if (!acceptedFormats.includes(file.type)) {
      toast({
        content: `Formato de arquivo não suportado. Use apenas: JPEG, PNG, GIF, WebP ou SVG`,
        type: 'error',
      });
      return;
    }

    if (file.size > maxFileSize) {
      toast({
        content: MAX_FILE_SIZE_ERROR_MESSAGE,
        type: 'error',
      });
      return;
    }

    try {
      const { width, height } = await getImageDimensions(file);

      if (width < minDimension || height < minDimension) {
        toast({
          content: MIN_DIMENSION_ERROR_MESSAGE,
          type: 'error',
        });
        return;
      }

      const base64 = await createBase64Image(file);
      onUpload(base64);
    } catch {
      toast({
        content: `Erro ao processar imagem: ${file.type} não suportado`,
        type: 'error',
      });
    }
  };

  return (
    <FileUploader accept={ACCEPTED_IMAGE_FORMATS} validate={validation}>
      {children}
    </FileUploader>
  );
};
